import { useEffect, useState, lazy, Suspense } from 'react'
import { ArrowBackIcon, ArrowForwardIcon, CloseIcon } from '@chakra-ui/icons'
import { Button, chakra, Spinner, Text, VStack } from '@chakra-ui/react'
import useBreakPoint from 'hooks/useBreakPoint'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'

// Lazy load ReactPlayer for better performance
const ReactPlayer = lazy(() => import('react-player'))
import Iframe from 'components/Iframe'
import { convertToEmbedLink, getOfficeAppLiveUrl } from 'components/MediaTab/utils'
import SvgIcon from 'components/SvgIcon'
import { S3FileTypeEnum } from 'constants/aws'
import { DARK_INPUT_BORDER, DARK_TEXT_MUTED, DARK_TEXT_PRIMARY } from 'constants/dark-tokens'
import { MediaType } from 'constants/media'
import { EBreakPoint } from 'constants/theme'
import { IMedia } from 'interfaces/media'
import { getS3FileUrlByMediaType, getValidArray } from 'utils/common'
import { getS3FileUrl } from 'utils/images'
import { ModalContainer, ModalContent, Slide, ImgBox, PrevButton, NextButton, CloseButton } from './lightBox.styles'

interface ILightBoxProps {
  mediaList: IMedia[]
  showModal: boolean
  imageIndex: number
  onCloseModal: () => void
  defaultImageUrl?: string
  hasNoBackground?: boolean
}

const LightBox = ({ mediaList, showModal, imageIndex = 0, onCloseModal }: ILightBoxProps) => {
  const { organizationStore } = useStores()
  const validList: IMedia[] = getValidArray<IMedia>(mediaList)
  const [mediaIndex, setMediaIndex] = useState<number>(imageIndex)
  const currentMedia: IMedia = validList?.[mediaIndex]
  const isMobile: boolean = useBreakPoint(EBreakPoint.BASE, EBreakPoint.MD)

  function handleClickPrev(): void {
    setMediaIndex(mediaIndex === 0 ? validList.length - 1 : mediaIndex - 1)
  }
  function handleClickNext(): void {
    setMediaIndex(mediaIndex === validList.length - 1 ? 0 : mediaIndex + 1)
  }

  useEffect(() => {
    setMediaIndex(imageIndex)
  }, [imageIndex])

  return (
    <ModalContainer showModal={showModal}>
      {currentMedia?.mediaType !== MediaType.EMBED_LINK && (
        <Button
          gap={2}
          height={12}
          variant="outline"
          color={DARK_TEXT_PRIMARY}
          background="rgba(255, 255, 255, 0.10)"
          border={`1px solid ${DARK_INPUT_BORDER}`}
          borderRadius="24px"
          fontSize="18px"
          fontWeight={500}
          lineHeight={7}
          position="absolute"
          top={6}
          right={84}
          zIndex={2}
          _hover={{ background: 'rgba(255, 255, 255, 0.18)' }}
          _active={{ background: 'rgba(255, 255, 255, 0.24)' }}
          _focus={{ boxShadow: 'none' }}
          sx={{ '& svg path': { fill: '#FFFFFF' } }}
        >
          <SvgIcon iconName="download" size={20} color={DARK_TEXT_PRIMARY} />
          <chakra.a
            color={DARK_TEXT_PRIMARY}
            target="_blank"
            href={getS3FileUrlByMediaType(currentMedia, organizationStore.organization?.id ?? 0)}
            rel="noreferrer"
            _hover={{ color: DARK_TEXT_PRIMARY, textDecoration: 'none' }}
            onClick={event => event.stopPropagation()}
          >
            Download
          </chakra.a>
        </Button>
      )}
      <CloseButton onClick={onCloseModal}>
        <CloseIcon />
      </CloseButton>
      <ModalContent background="transparent">
        {currentMedia && (
          <Slide key={currentMedia?.url} display="flex" justifyContent="center">
            {currentMedia?.mediaType === MediaType.IMAGE && (
              <ImgBox
                alt={currentMedia?.name}
                src={getS3FileUrl(
                  S3FileTypeEnum.IMAGE,
                  organizationStore.organization?.id ?? 0,
                  currentMedia?.image ?? currentMedia?.originalImage
                )}
              />
            )}
            {currentMedia?.mediaType === MediaType.VIDEO && (
              <Suspense fallback={<Spinner size="xl" color="white" />}>
                <ReactPlayer
                  width={isMobile ? '100%' : '1000px'}
                  height={isMobile ? '300px' : '700px'}
                  url={getS3FileUrl(
                    S3FileTypeEnum.VIDEO,
                    organizationStore.organization?.id ?? 0,
                    currentMedia?.video || currentMedia?.url || ''
                  )}
                  controls
                />
              </Suspense>
            )}
            {currentMedia?.mediaType === MediaType.FILE &&
              (isMobile ? (
                /**
                 * Office Live and the Docs viewer both render an empty white page
                 * inside a phone-width frame — which is what a coach was left
                 * staring at. Rather than embed a viewer that cannot draw here,
                 * the document is named and handed to the browser's own full
                 * screen, where it does open.
                 */
                <VStack
                  width="full"
                  spacing={3}
                  padding="24px"
                  borderRadius="12px"
                  border={`1px solid ${DARK_INPUT_BORDER}`}
                  background="rgba(255, 255, 255, 0.06)"
                >
                  <Text fontSize="md" fontWeight={600} color={DARK_TEXT_PRIMARY} textAlign="center" m={0}>
                    {currentMedia?.name || 'Document'}
                  </Text>
                  <Text fontSize="sm" color={DARK_TEXT_MUTED} textAlign="center" m={0}>
                    Documents preview full screen on a phone.
                  </Text>
                  <Button
                    as="a"
                    href={getOfficeAppLiveUrl(
                      getS3FileUrl(S3FileTypeEnum.OTHER, organizationStore.organization?.id ?? 0, currentMedia.document)
                    )}
                    target="_blank"
                    rel="noreferrer"
                    height="40px"
                    paddingX="16px"
                    borderRadius="24px"
                    border={`1px solid ${DARK_INPUT_BORDER}`}
                    background="rgba(255, 255, 255, 0.10)"
                    color={DARK_TEXT_PRIMARY}
                    fontSize="sm"
                    fontWeight={500}
                    _hover={{ background: 'rgba(255, 255, 255, 0.18)', textDecoration: 'none' }}
                    _focus={{ boxShadow: 'none' }}
                    onClick={event => event.stopPropagation()}
                  >
                    Open document
                  </Button>
                </VStack>
              ) : (
                <Iframe
                  src={getOfficeAppLiveUrl(
                    getS3FileUrl(S3FileTypeEnum.OTHER, organizationStore.organization?.id ?? 0, currentMedia.document)
                  )}
                  width="1000px"
                  height="700px"
                />
              ))}
            {currentMedia?.mediaType === MediaType.EMBED_LINK && (
              <Iframe
                id={convertToEmbedLink(String(currentMedia?.id) ?? '')}
                src={convertToEmbedLink(currentMedia?.url ?? '')}
                width={{ base: '100%', md: '1000px' }}
                height={{ base: '300px', md: '700px' }}
              />
            )}
          </Slide>
        )}
      </ModalContent>
      <PrevButton onClick={handleClickPrev} display={validList?.length > 1 ? 'flex' : 'none'}>
        <ArrowBackIcon />
      </PrevButton>
      <NextButton onClick={handleClickNext} display={validList?.length > 1 ? 'flex' : 'none'}>
        <ArrowForwardIcon />
      </NextButton>
    </ModalContainer>
  )
}
export default observer(LightBox)
